import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import ProblemStatements from './ProblemStatements';
import './App.css';

const navItems = [
  { id: 'about', label: 'About' },
  { id: 'tracks', label: 'Tracks' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'judging', label: 'Judging' },
  { id: 'memory', label: 'Memory' },
  { id: 'team', label: 'Team' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Contact' }
];


const stats = [
  { value: '150+', label: 'Hackers Expected' },
  { value: '30+', label: 'Competing Teams' },
  { value: '10H', label: 'Build Sprint' },
  { value: '2', label: 'Mentor Reviews' }
];

const tracks = [
  {
    name: 'AI / ML',
    desc: 'Build intelligent systems tackling real-world problems from fake news detection to fraud prevention and emotion analytics.'
  },
  {
    name: 'Blockchain',
    desc: 'Design decentralized solutions for lending, land registration, and identity verification.'
  },
  {
    name: 'Sustainability',
    desc: 'Create data-driven tools to optimize energy consumption and improve waste management systems.'
  },
  {
    name: 'Cyber Security',
    desc: 'Build robust systems for phishing detection, network intrusion detection, and malware classification.'
  }
];

const schedule = [
  { time: '10:00 AM', title: 'Check-in & Kickoff', note: 'Registration and opening brief.' },
  { time: '10:30 AM', title: 'Hacking Begins', note: 'Teams start building.' },
  { time: '11:30 AM - 12:30 PM', title: 'Mentor Review 1', note: 'Architecture and scope validation.' },
  { time: '1:00 PM', title: 'Lunch Break', note: 'Reset and network.' },
  { time: '4:30 PM - 5:30 PM', title: 'Mentor Review 2', note: 'Progress and stability review.' },
  { time: '6:30 PM', title: 'Code Freeze', note: 'Feature development closes.' },
  { time: '6:30 PM - 8:00 PM', title: 'Judging & Awards', note: 'Final demos and winner announcement.' }
];

const judgingCriteria = [
  {
    title: 'Technology',
    detail: 'Implementation depth, complexity, and effective use of tools.'
  },
  {
    title: 'Design',
    detail: 'Usability, interaction clarity, and practical user experience.'
  },
  {
    title: 'Completion',
    detail: 'A stable working prototype with implemented core features.'
  },
  {
    title: 'Learning',
    detail: 'Evidence of pushing beyond comfort zone and skill growth.'
  }
];

const memoryImages = [
  {
    src: 'https://res.cloudinary.com/ds3vepmkd/image/upload/w_1000,h_700,c_fill,q_auto:good,f_auto/eoorox/pictures_of_gallery/Hackstrom/DSC02934.jpeg',
    fallback: '/gallery1.jpg'
  },
  {
    src: 'https://res.cloudinary.com/ds3vepmkd/image/upload/w_1000,h_700,c_fill,q_auto:good,f_auto/eoorox/pictures_of_gallery/Hackstrom/DSC02938.jpeg',
    fallback: '/gallery2.jpg'
  },
  {
    src: 'https://res.cloudinary.com/ds3vepmkd/image/upload/w_1000,h_700,c_fill,q_auto:good,f_auto/eoorox/pictures_of_gallery/Hackstrom/DSC02946.jpeg',
    fallback: '/gallery3.jpg'
  },
  {
    src: 'https://res.cloudinary.com/ds3vepmkd/image/upload/w_1000,h_700,c_fill,q_auto:good,f_auto/eoorox/pictures_of_gallery/Hackstrom/DSC02947.jpeg',
    fallback: '/gallery4.jpg'
  },
  {
    src: 'https://res.cloudinary.com/ds3vepmkd/image/upload/w_1000,h_700,c_fill,q_auto:good,f_auto/eoorox/pictures_of_gallery/Hackstrom/IMG_8057.JPG',
    fallback: '/gallery5.jpg'
  }
];

const coordinators = [
  { name: 'Ootso Dhar Chowdhury', role: 'Student Coordinator', phone: '9593135858' },
  { name: 'Sambit Das', role: 'Student Coordinator', phone: '8240653185' },
  { name: 'Parthita Chattopadhay', role: 'Student Coordinator', phone: '7001088737' },
  { name: 'Priyam Kumar', role: 'Student Coordinator', phone: '8873932040' }
];

const volunteers = [
  'Sucheta Maity',
  'Sneha Roy',
  'Dibyojyoti Biswas',
  'Shaezah Iqbal'
];

const faqs = [
  {
    q: 'Who can participate?',
    a: 'Participation is open to currently enrolled college and university students.'
  },
  {
    q: 'What is the team size?',
    a: 'Teams must consist of 2 to 4 members.'
  },
  {
    q: 'Is there any entry fee?',
    a: 'No. HackStorm is a free offline hackathon.'
  },
  {
    q: 'Can AI tools be used?',
    a: 'Yes, AI tools are allowed. Teams should clearly disclose major tools used.'
  },
  {
    q: 'Can pre-existing code be reused?',
    a: 'Core project work should be built during the hackathon window.'
  }
];

const rules = [
  'This is an offline event with no entry fee.',
  'All participants must be currently enrolled students.',
  'All core project work should be done during official event hours.',
  'Libraries, APIs, and AI tools are permitted with transparent usage.',
  'Submitted project code must be publicly accessible.',
  'Organizer decisions on rule violations and disqualification are final.'
];

function AppInner() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPS = location.pathname === '/ps';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isRulesOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isRulesOpen]);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (isPS) {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleImageError = (e, fallback) => {
    if (e.currentTarget.src.endsWith(fallback)) return;
    e.currentTarget.src = fallback;
  };

  return (
    <div className="hs-site">
      <header className={`hs-header ${scrolled ? 'is-scrolled' : ''}`}>
        <button className="brand" onClick={() => scrollToSection('home')}>
          <img src="/logo.png" alt="HackStorm" className="brand-logo" />
          <span className="brand-text">
            HackStorm
            <small>TechStorm 2.26</small>
          </span>
        </button>

        <nav className={`hs-nav ${mobileMenuOpen ? 'open' : ''}`}>
          {navItems.map((item) => (
            <button key={item.id} onClick={() => scrollToSection(item.id)}>
              {item.label}
            </button>
          ))}
          <button
            className={`nav-ps-link ${isPS ? 'nav-ps-active' : ''}`}
            onClick={() => { setMobileMenuOpen(false); navigate('/ps'); }}
          >
            Problem Statements
          </button>
          <a
            href="https://hack-storm.devfolio.co"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-apply"
          >
            Apply
          </a>
        </nav>

        <button
          className={`menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <main>
        <Routes>
          <Route path="/ps" element={<ProblemStatements />} />
          <Route path="*" element={<MainContent
            stats={stats}
            tracks={tracks}
            schedule={schedule}
            judgingCriteria={judgingCriteria}
            memoryImages={memoryImages}
            coordinators={coordinators}
            volunteers={volunteers}
            faqs={faqs}
            rules={rules}
            activeFaq={activeFaq}
            setActiveFaq={setActiveFaq}
            isRulesOpen={isRulesOpen}
            setIsRulesOpen={setIsRulesOpen}
            handleImageError={handleImageError}
          />} />
        </Routes>
      </main>

      <footer className="hs-footer">
        <p>Hosted by B. P. Poddar Institute of Management and Technology</p>
        <p>Official Hackathon of TechStorm 2.26</p>
        <a href="mailto:hackstorm@bppimt.ac.in">hackstorm@bppimt.ac.in</a>
        <small>© 2026 HackStorm</small>
      </footer>

      <AnimatePresence>
        {isRulesOpen && (
          <motion.div
            className="rules-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsRulesOpen(false)}
          >
            <motion.div
              className="rules-card edge-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rules-head">
                <h3>HackStorm Rules Snapshot</h3>
                <button onClick={() => setIsRulesOpen(false)} aria-label="Close rules dialog">
                  ×
                </button>
              </div>
              <ul>
                {rules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MainContent({
  stats, tracks, schedule, judgingCriteria, memoryImages,
  coordinators, volunteers, faqs, rules,
  activeFaq, setActiveFaq, isRulesOpen, setIsRulesOpen, handleImageError
}) {
  return (
    <>
        <section id="home" className="hero-section">
          <div className="hero-bg">
            <img src="/hackstorm-banner.png" alt="HackStorm event atmosphere" />
          </div>

          <div className="container hero-layout">
            <motion.div
              className="hero-copy edge-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="hero-kicker">Official Hackathon of TechStorm 2.26</p>
              <h1>
                Build in the eye of <span>HackStorm</span>
              </h1>
              <p className="hero-description">
                An intense one-day offline hackathon where teams build, iterate, and demo under real
                constraints. High focus, strong execution, and working products matter.
              </p>

              <div className="hero-pills">
                <span>9 April 2026</span>
                <span>Main Auditorium, BPPIMT</span>
                <span>Team Size: 2-4</span>
              </div>

              <div className="hero-actions">
                <a
                  href="https://hack-storm.devfolio.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-devfolio"
                >
                  Apply with Devfolio
                </a>
              </div>
            </motion.div>

            <motion.aside
              className="hero-panel edge-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <img src="/hackstorm-hero.png" alt="HackStorm mascot" className="hero-mascot" />
              <h3>Competition Focus</h3>
              <ul>
                <li>Two structured mentor checkpoints</li>
                <li>Live demo in front of judges</li>
                <li>Execution quality over pitch hype</li>
                <li>Builder-first evaluation criteria</li>
              </ul>
            </motion.aside>
          </div>

          <div className="container stats-grid">
            {stats.map((item) => (
              <div key={item.label} className="stat-card edge-card">
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="about" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>About HackStorm</p>
              <h2>Serious build environment</h2>
            </div>

            <div className="about-layout">
              <p className="about-text edge-card">
                HackStorm is TechStorm&apos;s flagship building competition where teams work inside a fixed
                sprint window, undergo mentor reviews, and deliver a final live demo. It is designed for
                teams who want to build something real under time pressure, iterate quickly, and present a
                working product with strong implementation quality and clear utility.
              </p>

              <div className="about-cards">
                <article className="edge-card">
                  <h3>Offline by design</h3>
                  <p>Real-time collaboration with mentors and judges.</p>
                </article>
                <article className="edge-card">
                  <h3>Milestone-driven</h3>
                  <p>Two review rounds keep teams on technical track.</p>
                </article>
                <article className="edge-card">
                  <h3>Demo-first</h3>
                  <p>Working product beats theoretical ideas.</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section id="tracks" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Build Tracks</p>
              <h2>Problem spaces for teams</h2>
            </div>

            <div className="tracks-grid">
              {tracks.map((track) => (
                <article key={track.name} className="track-card edge-card">
                  <h3>{track.name}</h3>
                  <p>{track.desc}</p>
                </article>
              ))}
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <a href="/ps" className="btn btn-secondary" style={{ display: 'inline-block' }}>
                View All Problem Statements →
              </a>
            </div>
          </div>
        </section>

        <section id="schedule" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Event Timeline</p>
              <h2>One day. High velocity.</h2>
            </div>

            <div className="timeline">
              {schedule.map((item, idx) => (
                <article key={item.time} className="timeline-item edge-card">
                  <div className="timeline-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="timeline-content">
                    <span>{item.time}</span>
                    <h3>{item.title}</h3>
                    <p>{item.note}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="judging" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Judging</p>
              <h2>How winners are selected</h2>
            </div>

            <div className="judging-grid">
              {judgingCriteria.map((criterion) => (
                <article key={criterion.title} className="judging-card edge-card">
                  <h3>{criterion.title}</h3>
                  <p>{criterion.detail}</p>
                </article>
              ))}
            </div>

            <div className="judging-panel edge-card">
              <div>
                <h3>Submission baseline</h3>
                <p>Public repo, working demo, and transparent tool usage disclosure.</p>
              </div>
              <button className="btn btn-secondary" onClick={() => setIsRulesOpen(true)}>
                Read Rules Snapshot
              </button>
            </div>
          </div>
        </section>

        <section id="memory" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>HackStorm Memory</p>
              <h2>From previous editions</h2>
            </div>

            <div className="gallery-grid">
              {memoryImages.map((image, idx) => (
                <figure key={`${image.src}-${idx}`} className="gallery-card edge-card">
                  <img
                    src={image.src}
                    alt={`HackStorm memory ${idx + 1}`}
                    loading="lazy"
                    onError={(e) => handleImageError(e, image.fallback)}
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="sponsors" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Sponsors</p>
              <h2>Announcing Soon</h2>
            </div>
          </div>
        </section>

        <section id="team" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Team</p>
              <h2>Coordinators & Volunteers</h2>
            </div>

            <div className="contact-grid">
              {coordinators.map((person) => (
                <article key={person.phone} className="contact-card edge-card">
                  <h3>{person.name}</h3>
                  <p>{person.role}</p>
                  <a href={`tel:${person.phone}`}>+91 {person.phone}</a>
                </article>
              ))}
            </div>

            <div className="volunteer-showcase edge-card">
              {volunteers.map((name) => (
                <article key={name} className="volunteer-card edge-card">
                  <h3>{name}</h3>
                  <p>Volunteer</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>FAQs</p>
              <h2>Before you register</h2>
            </div>

            <div className="faq-list">
              {faqs.map((faq, idx) => (
                <article key={faq.q} className={`faq-item edge-card ${activeFaq === idx ? 'open' : ''}`}>
                  <button onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <b>{activeFaq === idx ? '-' : '+'}</b>
                  </button>
                  <motion.div
                    className="faq-answer-wrap"
                    initial={false}
                    animate={activeFaq === idx ? 'open' : 'closed'}
                    variants={{
                      open: { height: 'auto', opacity: 1 },
                      closed: { height: 0, opacity: 0 }
                    }}
                    transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <p>{faq.a}</p>
                  </motion.div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="section line-section">
          <div className="container">
            <div className="section-head">
              <p>Contact</p>
              <h2>Reach the coordinator desk</h2>
            </div>

            <div className="contact-grid">
              {coordinators.map((person) => (
                <article key={`contact-${person.phone}`} className="contact-card edge-card">
                  <h3>{person.name}</h3>
                  <p>{person.role}</p>
                  <a href={`tel:${person.phone}`}>+91 {person.phone}</a>
                </article>
              ))}
            </div>
          </div>
        </section>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
